import { NavLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useFeatureTheme } from "../../context/FeatureThemeContext";
import { Badge } from "../common/Badge";
import styles from "./Sidebar.module.css";

/**
 * Grouped nav (#215), reordered by USE rather than by build order (#320).
 *
 * The sections were named for how the app was built and ordered by when
 * each part arrived. Three things that cost a user something:
 *
 *   * Frequency was inverted. Checklists is used at every inspection and
 *     sat below Import Data, which you use once, ever.
 *   * "Apiary" was a junk drawer -- Team, Team Tasks, Journal, Import
 *     Data, Bloom and Checklists have nothing in common.
 *   * "Business" the SECTION collided with "Business" the TIER. The
 *     section held Free-tier items while the tier gated things in a
 *     different section, so a Harvester user read the heading as "all
 *     locked".
 *
 * Read top to bottom the order now runs roughly Free -> Harvester ->
 * Business, so the further down you go the more you are paying for. That
 * is deliberate: it makes the upgrade story legible WITHOUT naming a
 * section after a tier, which is what caused the collision.
 *
 * AI gets no section, and that is a decision rather than an omission.
 * Nine of the ten stubbed AI routes belong inside a page that already
 * exists -- health IQ on a hive, rotation suggestions on treatments,
 * predictive under Insights. Only assistant/ask is a destination, and it
 * earns ONE entry near the top when it is real (#324). An empty Ask is
 * worse than no Ask.

 *
 * Was originally a flat list where every business entry began
 * "Business: " -- the label doing a section header's job, badly, and
 * eating the width that should show the page name (#215).
 *
 * Each item keeps its `feature` key. Losing those when regrouping would
 * silently drop the Advanced markers and let people click into a gate.
 */
const NAV_SECTIONS = [
  {
    // No heading: the first group is where you land, and a header above
    // "Dashboard" states the obvious.
    label: null,
    items: [
      { to: "/", labelKey: "nav.dashboard", icon: "🏠" },
      { to: "/hives", labelKey: "nav.hives", icon: "🐝" },
    ],
  },
  {
    // Reordered by how OFTEN, not by how the app was built (#320).
    //
    // Checklists is used at every inspection and sat at the bottom of a
    // section, BELOW Import Data -- which you use once, ever. Journal was
    // buried the same way. Harvests moves out of "Business" because a
    // harvest is a hive event: nobody pulling supers thinks they are
    // doing admin.
    label: "At the hives",
    items: [
      { to: "/checklists", label: "Checklists", icon: "📋", feature: "HealthScoring" },
      // No feature gate: a private record of what you saw at your own
      // hives is not a premium capability, and a paid tier that owns
      // somebody's notes is a bad thing to have built (#265).
      { to: "/journal", label: "Journal", icon: "📓" },
      { to: "/business/harvests", label: "Harvests", icon: "🌾", feature: "HarvestProducts" },
      { to: "/bloom", labelKey: "nav.bloom", icon: "🌸", feature: "BloomCalendar" },
    ],
  },
  {
    // Was "Business", which collided with the Business TIER: this section
    // holds Free-tier items while the tier gates things in Insights
    // below. A Harvester user reasonably read the heading as "all
    // locked" (#320).
    //
    // "(Vendors, Expenditures)" until #445: EXPENDITURES STOPPED BEING
    // FREE and this comment did not notice. V109 (#387) put it behind
    // ExpenditureTracking so Free PREVIEWS it rather than has it, and
    // both /expenditures endpoints require the feature -- but the nav
    // item kept no `feature` key, so it showed no badge while the page
    // behind it refused. Reported as "Expenditures lacks the 'Harvester'
    // badge but inside the tab it asks you to go to 'Advanced'."
    //
    // Vendors IS still correctly here -- see its own note below.
    label: "Honey & money",
    items: [
      // HoneyPool, not HoneyChain: the POOL is Free (#247) while bottling
      // and everything downstream of a jar stays Harvester. The page shows
      // one or both depending on the tier, so gating the nav on the pool is
      // what matches what the page will actually render.
      //
      // The point of the split is that a Free account can put honey in AND
      // take it out. A pool that only grows stops matching the tank within
      // a season, and a number nobody can reconcile is worse than no number.
      { to: "/business/pool", label: "Honey Pool", icon: "🍯", feature: "HoneyPool" },
      /* WAX, PROPOLIS AND POLLEN (#430 slice 2), directly below Honey
         Pool because the two are the same kind of thing: a balance you
         add to, correct, package and sell from.

         ── IT USED TO BE EARNED, AND THAT WAS WRONG ──

         This carried `earned: "otherProducts"` until 5 Oct, so it was
         absent until the account had recorded a non-honey harvest --
         #430's rule that "an account that has never recorded a wax
         harvest should not see a wax screen."

         The flaw was pointed out by the owner looking for the feature
         and being unable to find it, which is the whole case in one
         sentence: you discovered you could sell wax by already having
         sold wax.

         What settles it is the line ABOVE. Honey Pool is gated on its
         FEATURE alone, so a paid account with an empty honey pool still
         sees it -- and nobody thinks that is clutter, because an empty
         pool is a thing you are about to fill. This was the only entry
         in the section held to a different standard, and there is no
         reason wax should be.

         HoneyChain is a paid feature, so the tier gate already does the
         job `earned` was added for: a free account never sees this, and
         a paying one is being shown a revenue line it is already paying
         for. The page's own empty state explains how a product gets
         there, which is where that explanation belongs. */
      { to: "/business/products", label: "Other Products Pool", icon: "🕯️",
        feature: "HoneyChain" },
      // Deliberately NOT gated. This page merged Clients into Vendors
      // (#215), and the client list is Free-tier ("business core"). The
      // honey balances and ledger INSIDE it are HoneyBusiness and hide
      // themselves -- gating the whole page would take the client list away
      // from the tier #186 promises it to.
      { to: "/business/vendors", label: "Vendors", icon: "🏪" },
      // The key that was missing. Same fix Suppliers needed below, and
      // the same symptom: no marker in the nav, and the gate only
      // appearing after somebody clicks through.
      { to: "/business/expenditures", label: "Expenditures", icon: "🧾", feature: "ExpenditureTracking" },
      { to: "/business/equipment", label: "Equipment", icon: "🧰", feature: "EquipmentManagement" },
      // Gated on EquipmentManagement, same as Equipment above -- SuppliersPage
      // wraps its table in that LockedFeature and both /suppliers endpoints
      // require it. The key was missing here once, so the nav showed no
      // Advanced marker and the gate only appeared after clicking through.
      { to: "/business/suppliers", label: "Suppliers", icon: "🚚", feature: "EquipmentManagement" },
      { to: "/business/recurring-orders", label: "Recurring Orders", icon: "🔁", feature: "BusinessAdvanced" },
    ],
  },
  {
    // ABOVE INSIGHTS SINCE 2 OCT, and the order it replaced was not
    // wrong when it was written -- it predates #445.
    //
    // Team was gated on TeamCollaboration, which shipped
    // default_enabled = 0. V113 turned it ON, because Free is SOLD two
    // seats and was not getting them. So this went from a section most
    // accounts could not use to one every account can, and nothing
    // moved it.
    //
    // Insights is the other half of the argument. Its own note calls
    // these "the occasional screens -- read at the end of a month, not
    // during a hive check", and for Free and Harvester it is almost
    // entirely locked: Profit & Loss needs BusinessAdvanced and Reports
    // needs one of three features. So the old order put a mostly-locked
    // monthly section above a fully-available frequent one, which is
    // the exact thing #320 exists to correct -- "reordered by how
    // OFTEN, not by how the app was built".
    // Out of the old "Apiary" drawer, which held Team, Team Tasks,
    // Journal, Import Data, Bloom and Checklists -- six things with
    // nothing in common. #314's activity view lives ON the team dashboard,
    // so it needs no entry of its own.
    label: "Team",
    items: [
      { to: "/team", labelKey: "nav.team", icon: "👥", feature: "TeamCollaboration" },
      // /team/tasks had a route and NO link -- not here, not from the
      // team dashboard. The page works and carries the only "Assign
      // Task" button in the app, and was reachable solely by typing
      // the URL, which is why a keeper reported that assigning work to
      // a team member had disappeared. Exactly the class #284 exists
      // to sweep for, found by testing first.
      /* BOTH FLAGS, via featureAll (3 Oct).
         
         `feature` as a LIST means ANY (#316, for Reports). The server's
         tuple means ALL. Those are opposite rules, and this item needed
         the second one: every call TeamTasksPage makes -- listTasks,
         listTemplates, listHandoffs, createTask -- is gated on a pair
         like ("TeamCollaboration", "team_tasks").
         
         TeamCollaboration alone is FREE since V113 (#445), because Free
         is sold two seats, while team_tasks is Beekeeper. So a Free
         account saw this link and every call behind it refused. Offering
         a control that cannot work is the shape #443, #445 and #472 each
         fixed in turn.
         
         The /team dashboard above keeps the single flag deliberately: it
         still manages SHARES, and account/shares carries no feature gate
         at all, so that page is genuinely useful to Free and degrades its
         Beekeeper sections rather than disappearing -- #254's rule,
         "withholds the AMOUNTS rather than the page". */
      { to: "/team/tasks", label: "Team Tasks", icon: "✅",
        featureAll: ["TeamCollaboration", "team_tasks"] },
    ],
  },
  {
    // Was "Analysis". "Insights" says what you get rather than what the
    // page does, and these are the occasional screens -- read at the end
    // of a month, not during a hive check.
    label: "Insights",
    items: [
      /* money: the endpoints behind it are financials=True, so a helper
         whose owner has hidden money gets a 403 (#472). Marked rather
         than feature-gated because it is NOT a tier boundary -- see
         the note beside isHidden below. */
      { to: "/business/pnl", label: "Profit & Loss", icon: "📈", feature: "BusinessAdvanced", money: true },
      /* BREAK-EVEN FOLDED INTO PROFIT & LOSS (#453). It had a tab while it
         was a TABLE of per-equipment analyses; #432 established that table
         can never have rows, so what was left was four figures -- a card,
         not a destination. It is also literally an aggregate of the P&L
         beside it, which is the #402 shape. A post-launch review (#456)
         may give it a period selector and its tab back. */
      /* FORECAST IS HIDDEN UNTIL IT HAS A MODEL (#449). app.RevenueForecasts
         is read by business/forecast and written by NOTHING -- no INSERT
         anywhere in the repository, not even a test. So the page queried an
         always-empty table and rendered a confident empty answer.

         That reads as "your business has nothing to forecast" rather than
         "this is not built yet", which is worse than no page at all, and it
         is the #432 failure shape exactly: nothing errors, so nothing is
         noticed. Restore this line when #449 gives it something to forecast
         with. */
      // Still gated on Regulatory. #316 splits reports across tiers -- hive
      // reports at Harvester, money at Business -- and the gate must widen
      // WHEN those land, not before: a door to an empty room is worse than
      // no door.
      // ANY of these, not all (#316). Reports is no longer one tier's
      // page: the regulatory report is Regulatory, revenue is
      // HoneyBusiness (Business) and hive activity is QueenManagement
      // (Harvester). Gated on Regulatory alone, a Harvester user who has
      // paid for the hive-activity report would have no door to it --
      // the page would render something and the nav would say it did
      // not exist.
      //
      // Gate the nav on what the page will RENDER, which is #316's own
      // instruction and the reason this is a list.
      { to: "/reports", labelKey: "nav.reports", icon: "📄",
        feature: ["Regulatory", "HoneyBusiness", "QueenManagement"] },
    ],
  },
  {
    // Import Data moves here from the daily-work drawer: it is one-time
    // setup, not something you reach for at a hive.
    label: "Account",
    items: [
      // Ungated. An account that cannot get its data IN is an account
      // that never becomes a customer (#275).
      { to: "/import", label: "Import Data", icon: "📥" },
      { to: "/support", labelKey: "nav.support", icon: "🎧", feature: "SupportFeedback" },
      { to: "/settings", labelKey: "nav.settings", icon: "⚙️" },
    ],
  },
];

/*
 * A nav item's `feature` is a string, or a LIST meaning "any of these"
 * (#316).
 *
 * Reports is the case that needed it: the regulatory report is
 * Regulatory, revenue is HoneyBusiness and hive activity is
 * QueenManagement, so a single flag locks out somebody who has paid for
 * one of the three. Gate on what the page will RENDER.
 *
 * Both places that evaluate a lock go through here, so the collapsed
 * -section logic and the per-item logic cannot drift into two different
 * opinions about what "locked" means.
 */
function featuresOf(item) {
  /* featureAll included, so lockReason has a flag to explain with. The
     FIRST is the right one to name for an ALL item: the pairs are
     ("TeamCollaboration", "team_tasks") shaped, and the sub-flag has no
     user-facing name anybody would recognise -- "team_tasks is not on
     your plan" is worse than naming the tier. lockReason resolves a tier
     from either, so the message is the same; this just picks the
     readable one. */
  const all = item.featureAll || [];
  if (!item.feature) return all;
  return [...(Array.isArray(item.feature) ? item.feature : [item.feature]), ...all];
}

function isLocked(item, hasFeature) {
  /* featureAll means the SERVER requires every one of these -- its
     `feature` tuple semantics. Distinct from `feature` as a list, which
     means ANY (#316). Checked first because an item carrying featureAll
     is locked unless it has them all, which is the stricter test. */
  if (item.featureAll) {
    return !item.featureAll.every((f) => hasFeature(f));
  }
  const features = featuresOf(item);
  // Locked only when NONE of them is held. An item gated on three flags
  // is one page reachable three ways, not three requirements.
  return features.length > 0 && !features.some((f) => hasFeature(f));
}

/* HIDDEN IS NOT LOCKED, and the difference is the whole point (#472).
 *
 * A LOCKED item is a tier boundary: the answer is to upgrade, and the
 * badge says which plan. A HIDDEN one is the account owner having
 * unticked "can see money" for this person -- no plan changes that, and
 * offering an upgrade would sell somebody a tier that does nothing.
 *
 * So these render as unavailable with a different explanation, and the
 * remedy is a conversation with the owner rather than a payment. */
function isHidden(item, canSeeFinancials) {
  /* EXPLICITLY false, not merely falsy. undefined means "not answered
     yet" -- the theme payload has not landed, or a caller did not supply
     it -- and hiding a money screen from the account OWNER while their
     features load would be a worse failure than showing it a moment
     early. The server refuses regardless, so the safe direction here is
     to show and let the page handle the 403. */
  return Boolean(item.money) && canSeeFinancials === false;
}

/** The flag to explain a lock with -- the first, which is the one the
 *  page leads with. */
function firstFeature(item) {
  return featuresOf(item)[0];
}

/**
 * Items the account has not earned are ABSENT, not locked (#430).
 *
 * Distinct from isLocked on purpose. Locked means "your plan does not
 * include this", which an upgrade answers. Earned means "you have none
 * of this", which no upgrade answers -- so a padlock would offer to sell
 * something the tier already includes, for a product the account may
 * never handle.
 *
 * Driven by the theme payload rather than a fetch here, because a
 * sidebar that called a business endpoint to decide whether to draw one
 * link would pay for it on every page load.
 */
function isEarned(item, earnings) {
  if (!item.earned) return true;
  return (earnings[item.earned] || []).length > 0;
}

export function Sidebar({ open, onNavigate }) {
  const { t } = useTranslation();
  const { hasFeature, lockReason, canSeeFinancials, otherProducts, isShown } = useFeatureTheme();
  const earnings = { otherProducts: otherProducts || [] };

  return (
    <nav className={[styles.sidebar, open ? styles.open : ""].join(" ")} aria-label="Main navigation">
      {NAV_SECTIONS.map((section, i) => {
        /* A section where EVERY item is locked collapses to one row (#320).
           A Free account otherwise sees a wall of padlocks -- 13 of 17
           entries -- which buries the four pages it can actually use.
           One line saying what would unlock it keeps the upgrade
           discoverable without the wall.

           Only when ALL of them are locked: a section with one locked
           item among several usable ones is showing a real choice, and
           collapsing that would hide pages somebody has paid for. */
        /* FILTERED BEFORE THE LOCK ARITHMETIC, which matters: an
           unearned item is not a locked one, and counting it as either
           would skew `allLocked` and could collapse a section that has
           usable pages in it. */
        /* A helper's own narrowing of this account (#508): an item the
           account HAS but they chose not to see is left out, not locked --
           an upgrade prompt for something already paid for would be
           wrong. Items the account lacks are untouched. */
        const items = section.items
          .filter((it) => isEarned(it, earnings))
          .filter((it) => isLocked(it, hasFeature) || !isLocked(it, isShown));
        /* Nothing left to show: no heading over an empty list. */
        if (items.length === 0 && section.items.length > 0) return null;
        const locks = items.map((it) =>
          isLocked(it, hasFeature) ? lockReason(firstFeature(it)) : null);
        const allLocked = section.label && locks.length > 0 && locks.every(Boolean);
        /* The sentinel tier is deliberately silent elsewhere in this file
           -- offering an upgrade for something no plan sells is a promise
           we cannot keep -- so a section locked only by sentinels is left
           expanded rather than collapsed behind an offer. */
        const offer = allLocked ? locks.find((r) => r && r !== "never") : null;

        if (allLocked && offer) {
          return (
            <div key={section.label} className={styles.section}>
              <div className={styles.sectionLabel} aria-hidden="true">{section.label}</div>
              <ul className={styles.list} aria-label={section.label}>
                <li>
                  <NavLink
                    to="/settings"
                    onClick={onNavigate}
                    className={({ isActive }) => [styles.link, isActive ? styles.active : ""].join(" ")}
                  >
                    <span aria-hidden="true" className={styles.icon}>🔒</span>
                    <span className={styles.label}>Unlock with {offer}</span>
                  </NavLink>
                </li>
              </ul>
            </div>
          );
        }

        return (
        <div key={section.label || `section-${i}`} className={styles.section}>
          {section.label ? (
            // aria-hidden on the visual heading and aria-label on the list:
            // a screen reader gets the group name once, from the structure,
            // rather than reading a stray word before every set of links.
            <div className={styles.sectionLabel} aria-hidden="true">{section.label}</div>
          ) : null}
          <ul className={styles.list} aria-label={section.label || undefined}>
            {items.map((item) => {
              // hasFeature() already reflects the current theme (Basic has
              // nothing enabled, Advanced has everything, Custom is
              // per-toggle) via the merged enabled_features the backend
              // returns.
              const locked = isLocked(item, hasFeature);
              const reason = locked ? lockReason(firstFeature(item)) : null;
              const hidden = isHidden(item, canSeeFinancials);
              return (
                <li key={item.to}>
                  <NavLink
                    to={item.to}
                    onClick={onNavigate}
                    className={({ isActive }) => [styles.link, isActive ? styles.active : ""].join(" ")}
                  >
                    <span aria-hidden="true" className={styles.icon}>{item.icon}</span>
                    <span className={styles.label}>{item.labelKey ? t(item.labelKey) : item.label}</span>
                    {/* Now says WHICH, because the theme payload carries
                        each feature's min_tier and the account's own tier
                        (#243). "Not enabled" was true and useless: it read
                        the same whether upgrading was the answer or the
                        user had switched the feature off themselves, and a
                        paying customer saw it on things they had bought.

                        The sentinel tier is deliberately silent -- offering
                        an upgrade for something no plan sells would be a
                        promise we cannot keep. */}
                    {hidden && (
                      <Badge tone="neutral" title="The account owner has not given you access to financial information">
                        Hidden
                      </Badge>
                    )}
                    {!hidden && locked && reason && reason !== "never" && (
                      <Badge
                        tone="honey"
                        className={styles.lockBadge}
                        title={
                          reason === "included"
                            ? "Included in your plan, switched off — Settings › Theme"
                            : `Unlocks with the ${reason} plan`
                        }
                      >
                        {reason === "included" ? "Off" : reason}
                      </Badge>
                    )}
                  </NavLink>
                </li>
              );
            })}
          </ul>
        </div>
        );
      })}
    </nav>
  );
}
